import React, { useEffect, useRef } from 'react';

/**
 * Interactive fluid mesh-gradient background (same concept as the other ReHub
 * apps). Blobs drift on their own and gently follow the cursor.
 * Palette tuned to the HDR Photo gem logo: ruby red, sapphire blue, amber gold.
 */

interface Blob {
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    radius: number;
    baseHue: number;
    hue: number;
    saturation: number;
    lightness: number;
    alpha: number;
    speedX: number;
    speedY: number;
    phase: number;
    phaseSpeed: number;
}

const BLOB_COUNT = 5;

const FluidBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
    const blobsRef = useRef<Blob[]>([]);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const onMove = (e: MouseEvent) => {
            mouseRef.current.x = e.clientX / window.innerWidth;
            mouseRef.current.y = e.clientY / window.innerHeight;
            mouseRef.current.active = true;
        };
        const onLeave = () => {
            mouseRef.current.active = false;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseleave', onLeave);

        // HDR Photo: ruby, sapphire, sky, amber gold, deep blue
        const palette = [
            { hue: 2,   sat: 80, light: 52, alpha: 0.22 },  // ruby red
            { hue: 210, sat: 85, light: 52, alpha: 0.24 },  // sapphire blue (brand)
            { hue: 195, sat: 80, light: 55, alpha: 0.18 },  // sky
            { hue: 42,  sat: 90, light: 55, alpha: 0.20 },  // amber gold
            { hue: 224, sat: 78, light: 48, alpha: 0.18 },  // deep blue
        ];

        const blobs: Blob[] = [];
        for (let i = 0; i < BLOB_COUNT; i++) {
            const p = palette[i % palette.length];
            blobs.push({
                x: Math.random(),
                y: Math.random(),
                targetX: Math.random(),
                targetY: Math.random(),
                radius: 0.25 + Math.random() * 0.2,
                baseHue: p.hue,
                hue: p.hue,
                saturation: p.sat,
                lightness: p.light,
                alpha: p.alpha,
                speedX: (Math.random() - 0.5) * 0.0003,
                speedY: (Math.random() - 0.5) * 0.0003,
                phase: Math.random() * Math.PI * 2,
                phaseSpeed: 0.003 + Math.random() * 0.004,
            });
        }
        blobsRef.current = blobs;

        const draw = () => {
            const w = canvas.width;
            const h = canvas.height;
            const mouse = mouseRef.current;

            // slate-900 base to match the app shell
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, w, h);

            for (const blob of blobs) {
                blob.phase += blob.phaseSpeed;
                blob.targetX += blob.speedX + Math.sin(blob.phase) * 0.0002;
                blob.targetY += blob.speedY + Math.cos(blob.phase * 0.7) * 0.0002;

                if (blob.targetX < -0.1 || blob.targetX > 1.1) blob.speedX *= -1;
                if (blob.targetY < -0.1 || blob.targetY > 1.1) blob.speedY *= -1;
                blob.targetX = Math.max(-0.2, Math.min(1.2, blob.targetX));
                blob.targetY = Math.max(-0.2, Math.min(1.2, blob.targetY));

                if (mouse.active) {
                    const dx = mouse.x - blob.targetX;
                    const dy = mouse.y - blob.targetY;
                    blob.targetX += dx * 0.008;
                    blob.targetY += dy * 0.008;
                    const hueShift = (mouse.x - 0.5) * 60 + (mouse.y - 0.5) * 40;
                    blob.hue = blob.baseHue + hueShift;
                } else {
                    blob.hue += (blob.baseHue - blob.hue) * 0.01;
                }

                blob.x += (blob.targetX - blob.x) * 0.02;
                blob.y += (blob.targetY - blob.y) * 0.02;

                const cx = blob.x * w;
                const cy = blob.y * h;
                const r = blob.radius * Math.max(w, h);

                const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
                const h1 = ((blob.hue % 360) + 360) % 360;
                grad.addColorStop(0, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness}%, ${blob.alpha})`);
                grad.addColorStop(0.5, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.7}%, ${blob.alpha * 0.4})`);
                grad.addColorStop(1, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.5}%, 0)`);

                ctx.globalCompositeOperation = 'lighter';
                ctx.fillStyle = grad;
                ctx.fillRect(0, 0, w, h);
            }

            ctx.globalCompositeOperation = 'source-over';
            rafRef.current = requestAnimationFrame(draw);
        };

        rafRef.current = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseleave', onLeave);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="pointer-events-none fixed inset-0 -z-10"
            style={{ width: '100vw', height: '100vh' }}
            aria-hidden="true"
        />
    );
};

export default FluidBackground;
